import React, { memo, useMemo } from 'react'
import { CirclePlusMajor, CustomersMajor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { addS } from 'src/utils/baseUtils'
import { TeamTableTab } from 'src/constants/team/enums'
import { useSelector } from 'react-redux'
import { getTeamMembersCount, getTeamMemberInvitesCount } from 'src/selectors/team'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { TableTabs } from 'src/components/shared/table/table-tabs/TableTabs'

interface Props {
  selectedTab: TeamTableTab | null
  onClick(tab: TeamTableTab): void
}

const TeamTabsComponent = ({ selectedTab, onClick }: Props) => {
  const teamMembersCount = useSelector(getTeamMembersCount) || 0
  const teamMemberInvitesCount = useSelector(getTeamMemberInvitesCount) || 0

  const { hasPermission } = useCheckPermissions()

  const availableTabs = useMemo(() => {
    const tabs = []

    if (hasPermission(UserPermission.UsersView)) {
      tabs.push({
        value: TeamTableTab.Users,
        title: 'Users on board',
        renderIcon: (selected: boolean) => (
          <CustomersMajor
            width={20}
            height={20}
            fill={selected ? colors.PRIMARY_PURPLE_600 : colors.GRAY_BLACK}
          />
        ),
      })
    }

    if (hasPermission(UserPermission.InvitesView)) {
      tabs.push({
        value: TeamTableTab.Invites,
        title: 'Invited users',
        renderIcon: (selected: boolean) => (
          <CirclePlusMajor
            width={20}
            height={20}
            fill={selected ? colors.PRIMARY_PURPLE_600 : colors.GRAY_BLACK}
          />
        ),
      })
    }

    return tabs.map((item) => {
      const counts = item.value === TeamTableTab.Users ? teamMembersCount : teamMemberInvitesCount

      return {
        ...item,
        dataTestId: `my-team-tab-${item.value}`,
        subtitle: `${counts || 0} ${addS(counts, 'user')}`,
      }
    })
  }, [teamMembersCount, teamMemberInvitesCount, hasPermission])

  return <TableTabs tabs={availableTabs} selectedValue={selectedTab} onClick={onClick} />
}

export const TeamTabs = memo(TeamTabsComponent)
